import * as Y from "yjs";
import { isLocalOrigin } from "./origins";

export type ChangeOrigin = "local" | "remote";

export type EntityChangeEvent =
  | { kind: "edit"; entityId: string; origin: ChangeOrigin }
  | { kind: "structural"; entityId: string; origin: ChangeOrigin }
  | { kind: "add"; entityId: string; origin: ChangeOrigin }
  | { kind: "delete"; entityId: string; origin: ChangeOrigin }
  | { kind: "story"; origin: ChangeOrigin }
  | { kind: "version-data"; key: string; origin: ChangeOrigin };

type Listener = (event: EntityChangeEvent) => void;

// Distributive omit: keep each union member's own fields (e.g. entityId) rather
// than collapsing to the keys common to all members, which is what a plain
// Omit<EntityChangeEvent, "origin"> would do.
type WithoutOrigin<T> = T extends unknown ? Omit<T, "origin"> : never;

// Position/membership lives in per-entity fields now (see schema.ts ordering
// model), so a "structural" change is a write to parentId or order.
const STRUCTURAL_KEYS = new Set(["parentId", "order"]);

export const observeEntityChanges = (
  doc: Y.Doc,
  options: { onEntityChange: Listener },
) => {
  const entities = doc.getMap<Y.Map<unknown>>("entities");
  const story = doc.getMap("story");

  const entitiesHandler = (events: Y.YEvent<any>[], transaction: Y.Transaction) => {
    const origin: ChangeOrigin = isLocalOrigin(transaction.origin)
      ? "local"
      : "remote";

    const emit = (event: WithoutOrigin<EntityChangeEvent>) => {
      // A local EDIT is already applied to Redux by the originating reducer;
      // re-dispatching it would clobber in-progress typing, so suppress it.
      // A local STRUCTURAL/add/delete change, by contrast, must still be
      // reconciled from the *merged* Y.Doc — the originator's optimistic local
      // splice can mispredict a concurrent move, and reconciling guarantees it
      // converges to the exact same tree as its peers.
      if (origin === "local" && event.kind === "edit") return;
      options.onEntityChange({ ...event, origin } as EntityChangeEvent);
    };

    for (const event of events) {
      if (event.target === entities) {
        event.keys.forEach((change, key) => {
          if (change.action === "add") emit({ kind: "add", entityId: key });
          else if (change.action === "delete") emit({ kind: "delete", entityId: key });
          // A wholesale entity replacement (set on an existing id) is structural
          // — it can change parentId/order, not just settings.
          else emit({ kind: "structural", entityId: key });
        });
        continue;
      }

      const path = event.path; // [entityId, ...] — relative to the entities map
      const entityId = path[0];
      if (typeof entityId !== "string") continue;

      const pathIsStructural =
        typeof path[1] === "string" && STRUCTURAL_KEYS.has(path[1] as string);
      const keysAreStructural =
        event.target instanceof Y.Map &&
        Array.from(event.keys.keys()).some((key) => STRUCTURAL_KEYS.has(key));

      if (pathIsStructural || keysAreStructural) {
        emit({ kind: "structural", entityId });
      } else {
        emit({ kind: "edit", entityId });
      }
    }
  };

  const storyHandler = (_events: Y.YEvent<any>[], transaction: Y.Transaction) => {
    // Story-level settings (design system, custom colors, anchors…) are applied
    // to Redux by the originating reducer, so only remote changes reconcile.
    if (isLocalOrigin(transaction.origin)) return;
    options.onEntityChange({ kind: "story", origin: "remote" });
  };

  // Per-version dynamic-variable values (the in-editor variable popover).
  // Flat key → value map; like entity edits, local writes are already in
  // Redux, so only remote changes are forwarded.
  const versionData = doc.getMap("versionData");
  const versionDataHandler = (
    event: Y.YMapEvent<unknown>,
    transaction: Y.Transaction,
  ) => {
    if (isLocalOrigin(transaction.origin)) return;
    event.keys.forEach((_change, key) => {
      options.onEntityChange({ kind: "version-data", key, origin: "remote" });
    });
  };

  entities.observeDeep(entitiesHandler);
  story.observeDeep(storyHandler);
  versionData.observe(versionDataHandler);
  return () => {
    entities.unobserveDeep(entitiesHandler);
    story.unobserveDeep(storyHandler);
    versionData.unobserve(versionDataHandler);
  };
};
